import parseHtml from 'html-react-parser';
import { observer } from 'mobx-react-lite';
import { renderAsync } from 'pele/lazy';
import {
  type ReactNode,
  useCallback,
  useContext,
  useRef,
  useState,
  useEffect,
  useMemo,
} from 'react';
import { v4 as uuid } from 'uuid';

import { MermaidPreviewModal } from '@/components/modals/MermaidPreviewModal';
import { ModalsManager } from '@/components/modals/modalsManager';
import { Skeleton } from '@/components/ui/skeleton';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { useInViewport } from '@/hooks/useInViewport';
import { settingsStore } from '@/stores/settings.store';

import { StaticExportContext } from '../../static-export-context';

import {
  MermaidDiagramView,
  RENDER_FAILED,
  mermaidCache,
  mermaidCacheKey,
  manageCacheSize,
} from './MermaidDiagramView';
import { type StaticCodeProps } from './StaticCode';
import { SyntaxHighlighter } from './SyntaxHighlighter';

// Pele measures and caches label widths on a canvas, which uses a fallback font until the
// requested font has loaded.
async function loadDiagramFonts(fontFamily: string) {
  await Promise.all(
    ['16px', 'bold 16px'].map((font) =>
      document.fonts.load(`${font} ${fontFamily}`).catch(() => [])
    )
  );
}

export const Mermaid = observer(function Mermaid({
  children: content,
}: StaticCodeProps) {
  const [diagram, setDiagram] = useState<ReactNode>();
  const [codeBlockFallback, setCodeBlockFallback] = useState(false);
  const fallbackTimer = useRef<ReturnType<typeof setTimeout>>(null);
  const renderGeneration = useRef(0);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const source = typeof content === 'string' ? content : '';
  const dyslexicFont = settingsStore.dyslexicFont;
  const cacheKey = useMemo(
    () => mermaidCacheKey(source, dyslexicFont),
    [source, dyslexicFont]
  );

  useEffect(() => {
    renderGeneration.current++;
    setDiagram(undefined);
    setCodeBlockFallback(false);
    return () => {
      if (fallbackTimer.current) clearTimeout(fallbackTimer.current);
    };
  }, [cacheKey]);

  const isStaticExport = useContext(StaticExportContext);
  const { ref: inViewportRef, inViewport } = useInViewport({
    enabled: !isStaticExport,
  });
  const setContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      containerRef.current = node;
      inViewportRef(node);
    },
    [inViewportRef]
  );

  const renderImage = useDebouncedCallback(
    async (fontFamily: string | undefined) => {
      const gen = renderGeneration.current;
      try {
        if (fontFamily) await loadDiagramFonts(fontFamily);
        const { svg } = await renderAsync(source, {
          fontFamily,
          idPrefix: `pele-${uuid()}`,
          // Diagram links would bypass the external-link confirmation that markdown links get.
          links: false,
        });
        if (gen !== renderGeneration.current) return;
        const image = parseHtml(svg);

        setDiagram(image);
        mermaidCache.set(cacheKey, image);
        manageCacheSize();
      } catch (_error) {
        if (gen !== renderGeneration.current) return;
        fallbackTimer.current = setTimeout(() => {
          setCodeBlockFallback(true);
          mermaidCache.set(cacheKey, RENDER_FAILED);
          manageCacheSize();
        }, 500);
      }
    },
    {
      delay: 250,
      flushOnUnmount: true,
    }
  );

  useEffect(() => {
    if (!inViewport) return;

    if (mermaidCache.has(cacheKey)) {
      const cached = mermaidCache.get(cacheKey);

      // Move to end for LRU
      mermaidCache.delete(cacheKey);
      mermaidCache.set(cacheKey, cached);

      if (cached === RENDER_FAILED) {
        setCodeBlockFallback(true);
      } else {
        setDiagram(cached);
      }
      return;
    }

    // Pele measures label widths with this font, so it must match the font the SVG inherits.
    // It is read here because a render flushed on unmount runs after the container detaches.
    const fontFamily = containerRef.current
      ? getComputedStyle(containerRef.current).fontFamily
      : undefined;
    renderImage(fontFamily);
  }, [cacheKey, inViewport, renderImage]);

  const copyText = useMemo(() => `\`\`\`mermaid\n${source}\n\`\`\``, [source]);

  const handleExpand = useCallback(() => {
    ModalsManager.show(MermaidPreviewModal, {
      content: source,
    });
  }, [source]);

  if (codeBlockFallback)
    return <SyntaxHighlighter language="mermaid" code={source} />;

  return (
    <div
      className="w-full h-[35vh] max-h-[400px]"
      data-no-copy="true"
      data-render-pending={isStaticExport && !diagram ? 'diagram' : undefined}
      ref={setContainerRef}
    >
      {inViewport && (
        <>
          {!diagram && (
            <div className="h-full motion-opacity-in-0 motion-duration-500">
              <Skeleton className="h-full" />
            </div>
          )}
          {diagram && (
            <MermaidDiagramView
              diagram={diagram}
              copyText={copyText}
              onExpand={handleExpand}
            />
          )}
        </>
      )}
    </div>
  );
});
